import { PageQuery, toQueryParam } from "./pageQuery";

/**
 * The query string of GET users/vendor-users for the admin "Users" page
 * (flag REACT_APP_ADMIN_USER_SEARCH_ENABLED, round 2; paired with the API
 * flag VendorUsersSearchV2Enabled, whose query parameters these are:
 * sort=name|email|organization|lastLogin, dir=asc|desc, vendorOrgId=<id> -
 * the API ignores all three with its flag off).
 *
 * Without options the string IS today's toQueryParam: the flag-off call and
 * its cache key stay byte-identical. With options, only the values that are
 * set are appended, in a fixed order, so one state has exactly one key.
 */

export const VENDOR_USERS_SORT_FIELDS = ["name", "email", "organization", "lastLogin"] as const;
export type VendorUsersSortField = (typeof VENDOR_USERS_SORT_FIELDS)[number];
export type VendorUsersSortDir = "asc" | "desc";

export interface VendorUsersSort {
  field: VendorUsersSortField;
  dir: VendorUsersSortDir;
}

export interface VendorUsersSearchOptions {
  /** Absent = the API's default order (name ascending), exactly today's. */
  sort?: VendorUsersSort;
  /** Only the users of this vendor org and its customer orgs; absent = all. */
  vendorOrgId?: number;
}

export function isVendorUsersSortField(value: unknown): value is VendorUsersSortField {
  return typeof value === "string" && (VENDOR_USERS_SORT_FIELDS as readonly string[]).includes(value);
}

/**
 * The API parses vendorOrgId as a 32-bit integer (Int32.TryParse): above this
 * it reads "no filter" and would answer ALL users under the chip - so the
 * client never sends such an id.
 */
export const MAX_ORG_ID = 2_147_483_647;

/** The API validates vendorOrgId as a positive Int32; anything else is never sent. */
export function isVendorOrgId(value: unknown): boolean {
  return typeof value === "number" && Number.isInteger(value) && value > 0 && value <= MAX_ORG_ID;
}

export function vendorUsersQueryString(query: PageQuery, options?: VendorUsersSearchOptions): string {
  const base = toQueryParam(query);
  const extras: string[] = [];
  if (options?.sort) extras.push(`sort=${options.sort.field}`, `dir=${options.sort.dir}`);
  const vendorOrgId = options?.vendorOrgId;
  if (vendorOrgId !== undefined && isVendorOrgId(vendorOrgId)) extras.push(`vendorOrgId=${vendorOrgId}`);
  if (extras.length === 0) return base;
  return `${base}${base.length === 0 ? "?" : "&"}${extras.join("&")}`;
}
